import { useEffect, useRef, useState } from 'react';
import { isBrowser } from 'ws-scripts/modules/environment';
import logger from 'ws-scripts/modules/logger';

const LOG_PREFIX = '[ws-bodystyle-selector][useInvData]';
const TIMEOUT_MS = 5000;
const POLL_INTERVAL_MS = 100;

/**
 * Safely read the ws-inv-data API once it is usable.
 *
 * @returns {Object | null} The InvData, or null while it is absent or not yet
 * carrying getFacets.
 */
const getInvData = () => {
	if (!isBrowser) {
		return null;
	}

	const invData = window.DDC?.InvData;

	return typeof invData?.getFacets === 'function' ? invData : null;
};

/**
 * Hook that waits for ws-inv-data and returns its InvData once available.
 *
 * ws-inv-data attaches data to `window.DDC.InvData` whenever it finishes
 * loading, which may be before or after this widget mounts. The hook therefore
 * checks once on mount and then polls every POLL_INTERVAL_MS until the API
 * shows up, giving up after TIMEOUT_MS so a page without ws-inv-data cannot
 * leave the widget polling forever.
 *
 * `isReady` means InvData is present and carries a callable `getFacets`;
 * consumers can call it without re-checking.
 *
 * @returns {{ isReady: boolean, invData: Object | null, error: string | null }}
 */
export const useInvData = () => {
	const [state, setState] = useState({
		isReady: false,
		invData: null,
		error: null
	});
	const startedAt = useRef(null);

	useEffect(() => {
		if (!isBrowser) {
			return () => {};
		}

		let intervalId = null;
		let timeoutId = null;

		const clearTimers = () => {
			if (intervalId !== null) {
				clearInterval(intervalId);
				intervalId = null;
			}
			if (timeoutId !== null) {
				clearTimeout(timeoutId);
				timeoutId = null;
			}
		};

		const resolve = (invData) => {
			clearTimers();
			logger.debug(
				`${LOG_PREFIX} InvData available after ${
					Date.now() - startedAt.current
				}ms`
			);
			setState({ isReady: true, invData, error: null });
		};

		startedAt.current = Date.now();

		// Check if already available
		const existing = getInvData();
		if (existing) {
			resolve(existing);
			return clearTimers;
		}

		intervalId = setInterval(() => {
			const invData = getInvData();
			if (invData) {
				resolve(invData);
			}
		}, POLL_INTERVAL_MS);

		timeoutId = setTimeout(() => {
			clearTimers();
			const message = `window.DDC.InvData was not available after ${TIMEOUT_MS}ms`;
			logger.warn(`${LOG_PREFIX} ${message}`);
			setState({ isReady: false, invData: null, error: message });
		}, TIMEOUT_MS);

		return clearTimers;
	}, []);

	return state;
};

export default useInvData;
